import { CLASSES, classColor, classOf, classSkills, SHAPE_GLYPHS, type ClassId } from "@eldrun/content";

/**
 * Tela de escolha da classe (F85): a mesma no "Jogar sozinho" e no lobby. Partes puras (sem DOM nem
 * three): as colunas, quem escolheu o quê e onde cada modelo fica na tela.
 */

/** Quem escolheu a classe da coluna: o número (o hexágono), o nome e o pronto. */
export interface ClassPick {
  sessionId: string;
  n: number;
  name: string;
  ready: boolean;
  isMe: boolean;
}

export interface ClassColumn {
  id: ClassId;
  name: string;
  role: string;
  fantasy: string;
  /** Ícone da classe pela forma (⬡ △ ◯ ≡). */
  glyph: string;
  /** A cor da classe (a fixa; a tela passa pelo modo em uso). */
  color: string;
  /** Passiva, ativa e ultimate, com a descrição para a dica. */
  skills: { kind: string; name: string; description: string }[];
  /** A minha escolha. */
  active: boolean;
  /** Quem está nesta classe, pelo número. Repetidas valem: dividem a cor, o número e o nome distinguem. */
  picks: ClassPick[];
}

/** O que a tela lê de cada jogador (no lobby; sozinho, ninguém). */
export interface PickSource {
  sessionId: string;
  name: string;
  /** Número do jogador (1 a 4). */
  n: number;
  classId: string;
  ready: boolean;
  isMe: boolean;
}

/** As quatro colunas, na ordem da key art, com a minha escolha e as dos outros. */
export function classColumns(chosen: ClassId | undefined, players: readonly PickSource[] = []): ClassColumn[] {
  return CLASSES.map((c) => {
    const s = classSkills(c);
    return {
      id: c.id,
      name: c.name,
      role: c.role,
      fantasy: c.fantasy,
      glyph: SHAPE_GLYPHS[c.shape],
      color: classColor(c.id),
      skills: [
        { kind: "Passiva", name: s.passiva.name, description: s.passiva.description },
        { kind: "Ativa", name: s.ativa.name, description: s.ativa.description },
        { kind: "Ultimate", name: s.ultimate.name, description: s.ultimate.description },
      ],
      active: c.id === chosen,
      picks: players
        .filter((p) => classOf(p.classId).id === c.id)
        .map((p) => ({ sessionId: p.sessionId, n: p.n, name: p.name, ready: p.ready, isMe: p.isMe }))
        .sort((a, b) => a.n - b.n),
    };
  });
}

/** Retângulo na tela, em px CSS (o `getBoundingClientRect`). */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Câmera do palco: em (0, `y`, `dist`), olhando para −Z inclinada `pitch` rad para baixo (o chão aparece); o plano dos modelos é z = 0. */
export interface StageCamera {
  fov: number;
  dist: number;
  y: number;
  pitch?: number;
}

/** Onde e de que tamanho fica um modelo (no plano z = 0). */
export interface StagePlacement {
  x: number;
  y: number;
  scale: number;
}

/**
 * Põe o modelo de `height` m no palco `rect` da coluna dele: os pés no centro do pé do palco (o raio da
 * câmera por esse pixel até o plano z = 0) e a altura em ~78 % dele (nunca mais largo que a coluna). Assim
 * o modelo fica sob o botão da coluna (o mouse, o foco e o toque são do DOM) em qualquer tamanho de tela.
 */
export function stagePlacement(viewport: { w: number; h: number }, camera: StageCamera, rect: Rect, height: number): StagePlacement {
  const tanH = Math.tan((camera.fov * Math.PI) / 360);
  const aspect = viewport.w / Math.max(1, viewport.h);
  const pitch = camera.pitch ?? 0;
  const nx = ((rect.x + rect.w / 2) / Math.max(1, viewport.w)) * 2 - 1;
  const ny = 1 - ((rect.y + rect.h * 0.96) / Math.max(1, viewport.h)) * 2;
  // Direção do raio no mundo (direita, cima e frente da câmera inclinada) e o encontro com z = 0.
  const dx = nx * tanH * aspect;
  const dy = ny * tanH;
  const t = camera.dist / (dy * Math.sin(pitch) + Math.cos(pitch));
  const x = t * dx;
  const y = camera.y + t * (dy * Math.cos(pitch) - Math.sin(pitch));
  // Pixels por metro nessa profundidade (`t` é a distância ao longo da frente da câmera).
  const pixelsPerMeter = viewport.h / (2 * t * tanH);
  const target = Math.min(rect.h * 0.78, rect.w * 1.25);
  return { x, y, scale: Math.max(0.05, target / pixelsPerMeter / Math.max(0.1, height)) };
}
